import { useEffect, useRef } from 'react';

export const useFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = (keepFocus = true) => {
    if (!htmlElRef?.current) {
      return;
    }

    if (keepFocus) {
      htmlElRef.current.focus();
    } else {
      htmlElRef.current.blur();
    }
  };
  return [htmlElRef, setFocus];
};

/**
 * Adds keyboard accessibility to a component.
 * Pass through a list of objects with {key: string, action: func} and if it matches
 * the keycode it'll perform that function
 */
export const useKeyboardListener = (elementRef, onBlur, keyActions, deps) => {
  // Perform callbacks when key is pressed
  const handleKeyPress = (event) => {
    // Return early if this is not the active element
    if (document.activeElement !== elementRef.current) {
      return;
    }

    // event.preventDefault();

    // Blur on escape without selecting
    if (event.key === 'Escape') {
      document.activeElement.blur();
      onBlur();
    }

    keyActions.forEach((keyAction) => {
      if (event.key === keyAction?.key) {
        keyAction?.action(event);
      }
    });
  };

  // Add keyboard event listener to input
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  });

  return handleKeyPress;
};

/**
 * Skips triggering `fn` on the initial render (unless forced) but will call on subsequent updates to `inputs`
 * @param {() => void} fn
 * @param {[*]} inputs
 * @param {boolean} force
 */
export const useDidUpdateEffect = (fn, inputs, force) => {
  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current || (didMountRef.current === false && force)) {
      if (force) {
        didMountRef.current = true;
      }
      return fn();
    }
    didMountRef.current = true;
    return null;
  }, inputs);
};
